import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { TfiMenu, TfiClose } from 'react-icons/tfi';
import { FaEnvelope, FaLinkedinIn, FaInstagram, FaGithub } from 'react-icons/fa';
import './style.css';

const socialLinks = [
  { href: 'mailto:orhancodes@gmail.com', Icon: FaEnvelope, label: 'E-Mail' },
  { href: 'https://www.linkedin.com/in/orhan-yilmaz-codes', Icon: FaLinkedinIn, label: 'LinkedIn' },
  { href: 'https://www.instagram.com/orhancodes/', Icon: FaInstagram, label: 'Instagram' },
  { href: 'https://github.com/orhanDev', Icon: FaGithub, label: 'GitHub' }
];

const navItems = [
  { hash: 'home', label: 'Home' },
  { hash: 'about', label: 'Über mich' },
  { hash: 'what-to-do', label: 'Technologien' },
  { hash: 'resume', label: 'Portfolio' },
  { hash: 'contact', label: 'Kontakt' }
];

const MainMenu = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeHash, setActiveHash] = useState('home');
  const hamburgerRef = useRef(null);

  const toggleMenu = (e) => {
    e?.stopPropagation();
    setMenuOpen((open) => !open);
  };
  const closeMenu = () => setMenuOpen(false);

  const handleNavClick = (e, hash) => {
    e.preventDefault();
    closeMenu();
    setActiveHash(hash);
    setTimeout(() => {
      if (hash === 'home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.history.pushState('', '', '/');
      } else {
        const element = document.getElementById(hash);
        if (element) {
          const offsetTop = element.offsetTop - 80;
          window.scrollTo({ top: offsetTop, behavior: 'smooth' });
          window.history.pushState('', '', `#${hash}`);
        }
      }
    }, 150);
  };

  useEffect(() => {
    const syncHash = () => {
      const hash = window.location.hash.replace('#', '') || 'home';
      setActiveHash(hash);
    };
    syncHash();
    window.addEventListener('hashchange', syncHash);
    return () => window.removeEventListener('hashchange', syncHash);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && menuOpen) closeMenu();
    };

    if (menuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.classList.remove('menu-open');
    };
  }, [menuOpen]);

  const renderNavLinks = () => (
    <ul className="nav-links">
      {navItems.map(({ hash, label }) => (
        <li key={hash}>
          <a
            href={`#${hash}`}
            title={label}
            className={activeHash === hash ? 'active' : ''}
            onClick={(e) => handleNavClick(e, hash)}
          >
            {label}
          </a>
        </li>
      ))}
    </ul>
  );

  const renderNavSocial = () => (
    <div className="nav-social">
      {socialLinks.map(({ href, Icon, label }) => (
        <a
          key={label}
          href={href}
          target={href.startsWith('mailto:') ? undefined : '_blank'}
          rel={href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
          className="nav-social-link"
          title={label}
          aria-label={label}
        >
          <Icon />
        </a>
      ))}
    </div>
  );

  const mobileMenuPortal =
    typeof document !== 'undefined'
      ? createPortal(
          <>
            <div
              className={`menu-overlay ${menuOpen ? 'is-visible' : ''}`}
              onClick={closeMenu}
              aria-hidden={!menuOpen}
            />
            <div
              className={`mobile-nav-drawer ${menuOpen ? 'active' : ''}`}
              aria-hidden={!menuOpen}
            >
              {renderNavLinks()}
              {renderNavSocial()}
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <>
      {mobileMenuPortal}

      <nav className="main-nav" aria-label="Hauptnavigation">
        <div className="hamburger-menu" ref={hamburgerRef}>
          <button
            type="button"
            className={`lbl-menu ${menuOpen ? 'is-open' : ''}`}
            onClick={toggleMenu}
            aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <TfiClose /> : <TfiMenu />}
          </button>
        </div>

        <div className="desktop-nav">
          {renderNavLinks()}
          {renderNavSocial()}
        </div>
      </nav>
    </>
  );
};

export default MainMenu;
